// Settings → Old therapist codes (0337).
//
// When a therapist's code is replaced with an MT- code (reissue_therapist_codes,
// 0336), the old one keeps working for this many days so a client who wrote it
// down is not turned away the next morning. It counts from the day THAT code was
// replaced. 0 stops old codes at once. A current code is never affected, and with
// no settings row there is no grace at all. Superadmin only (the page and the
// table policy); every save is in the audit log.

import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { type OperatingSettings, useUpdateOperatingSettings } from "./settings.hooks";
import { Field, NumberInput } from "./settingsFields";

const DEFAULT_DAYS = 30;

export function TherapistCodeGraceCard({ settings }: { settings: OperatingSettings }) {
  const update = useUpdateOperatingSettings();
  const [draft, setDraft] = useState(() => String(settings.retired_therapist_code_grace_days));

  useEffect(() => {
    setDraft(String(settings.retired_therapist_code_grace_days));
  }, [settings]);

  const parsed = Number(draft);
  // The database refuses outside 0-3650 (0337); checked here so the reason is
  // readable before the round trip.
  const valid = draft.trim() !== "" && Number.isInteger(parsed) && parsed >= 0 && parsed <= 3650;

  function save() {
    if (!valid) {
      toast.error("The grace period has to be a whole number of days, 0 to 3650.");
      return;
    }
    update.mutate(
      { retired_therapist_code_grace_days: parsed },
      {
        onSuccess: () => toast.success("Old-code grace period saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  return (
    <Card className="gap-5">
      <CardHeader className="gap-2">
        <CardTitle className="type-title-body"><h2>Old therapist codes</h2></CardTitle>
        <CardDescription className="max-w-prose leading-relaxed">
          After a therapist&apos;s code is replaced with a new MT- code, the old one keeps working
          for this many days, counted from the day it was replaced. Set it to 0 to stop old codes
          straight away. A therapist&apos;s current code is never affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5">
        <Field label="Days an old code keeps working (0–3650)">
          <NumberInput value={draft} onChange={setDraft} />
        </Field>
        {valid && parsed !== DEFAULT_DAYS ? (
          <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
            The default is {DEFAULT_DAYS} days.{" "}
            {parsed === 0
              ? "At 0, an old code stops working the moment it is replaced."
              : `At ${parsed}, an old code works for ${parsed} ${parsed === 1 ? "day" : "days"} after it is replaced.`}
          </p>
        ) : null}
      </CardContent>
      <CardFooter className="justify-end">
        <Button onClick={save} disabled={update.isPending || !valid}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  );
}
